import { WithDisposable } from '@blocksuite/block-std';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  MindmapElementModel,
  type MindmapNode,
} from '../../../../surface-block/element-model/mindmap.js';
import type { EdgelessRootBlockComponent } from '../../edgeless-root-block.js';

type HighlightInfo = {
  nodeId: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

@customElement('edgeless-mindmap-current-highlight')
export class EdgelessMindmapCurrentHighlight extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
      z-index: 1;
    }

    .highlight-overlay {
      position: absolute;
      pointer-events: none;
      border-radius: 8px;
      overflow: hidden;
    }

    .shimmer-layer {
      position: absolute;
      inset: 0;
      border-radius: 8px;
      overflow: hidden;
    }

    .shimmer-layer::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.6),
        transparent
      );
      transform: translateX(-100%);
      animation: shimmer-sweep 2s linear infinite;
    }

    @keyframes shimmer-sweep {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(100%);
      }
    }
  `;

  @property({ attribute: false })
  accessor edgeless!: EdgelessRootBlockComponent;

  @state()
  private accessor _highlightInfo: HighlightInfo | null = null;

  private _animationTimeout: ReturnType<typeof setTimeout> | null = null;

  override connectedCallback() {
    super.connectedCallback();

    this._disposables.add(
      this.edgeless.service.viewport.viewportUpdated.on(() => {
        this.requestUpdate();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementUpdated.on(() => {
        if (this._highlightInfo) {
          this._findAndHighlightNode(this._highlightInfo.nodeId);
        }
      })
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._animationTimeout) {
      clearTimeout(this._animationTimeout);
      this._animationTimeout = null;
    }
  }

  highlightNode(nodeId: string, options?: { duration?: number }): void {
    const duration = options?.duration ?? 8000;

    if (this._animationTimeout) {
      clearTimeout(this._animationTimeout);
    }

    this._findAndHighlightNode(nodeId);

    if (duration > 0) {
      this._animationTimeout = setTimeout(() => {
        this.clearHighlight();
      }, duration);
    }
  }

  clearHighlight(): void {
    this._highlightInfo = null;
    if (this._animationTimeout) {
      clearTimeout(this._animationTimeout);
      this._animationTimeout = null;
    }
  }

  private _findAndHighlightNode(nodeId: string): void {
    const elements = (this.edgeless.surfaceBlockModel as unknown as { getElementsByType(type: string): unknown[] }).getElementsByType('mindmap');

    for (const element of elements) {
      if (!(element instanceof MindmapElementModel)) continue;

      const mindmap = element;
      let foundNode: MindmapNode | null = null;

      mindmap.traverse((node) => {
        if (node.id === nodeId) {
          foundNode = node;
        }
      });

      if (foundNode) {
        const bound = (foundNode as MindmapNode).element.elementBound;
        this._highlightInfo = {
          nodeId,
          x: bound.x,
          y: bound.y,
          width: bound.w,
          height: bound.h,
        };
        return;
      }
    }

    this._highlightInfo = null;
  }

  override render() {
    if (!this._highlightInfo) {
      return nothing;
    }

    const { viewport } = this.edgeless.service;
    const { zoom } = viewport;
    const info = this._highlightInfo;

    const [viewX, viewY] = viewport.toViewCoord(info.x, info.y);
    const viewWidth = info.width * zoom;
    const viewHeight = info.height * zoom;

    const padding = 6;

    return html`
      <div
        class="highlight-overlay"
        style=${styleMap({
          transform: `translate(${viewX - padding}px, ${viewY - padding}px)`,
          width: `${viewWidth + padding * 2}px`,
          height: `${viewHeight + padding * 2}px`,
        })}
      >
        <div class="shimmer-layer"></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-mindmap-current-highlight': EdgelessMindmapCurrentHighlight;
  }
}
